import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Plus, Eye, Edit, CreditCard, Building2, Calendar, DollarSign, Trash2, ArrowRight, Zap } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import AddSubscriptionModal from "./AddSubscriptionModal";

interface Subscription {
  id: number;
  tenant_id: number;
  plan_type: string;
  amount: number;
  currency: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
  school_name: string;
  subdomain: string;
  billing_email: string;
  school_status: string;
}

const SubscriptionsManagement: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  useEffect(() => {
    fetchSubscriptions();
  }, [statusFilter, planFilter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (planFilter !== "all") params.append("plan_type", planFilter);

      const response = await axios.get(
        getApiUrl(`superadmin/subscriptions?${params.toString()}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscriptions(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, string> = {
      active: "bg-emerald-50 text-emerald-700 border-emerald-100",
      cancelled: "bg-slate-50 text-slate-700 border-slate-100",
      past_due: "bg-amber-50 text-amber-700 border-amber-100",
      expired: "bg-rose-50 text-rose-700 border-rose-100"
    };
    return (
      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${configs[status] || configs.cancelled}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPlanBadge = (planType: string) => {
    const configs: Record<string, string> = {
      monthly: "bg-blue-50 text-blue-700 border-blue-100",
      quarterly: "bg-indigo-50 text-indigo-700 border-indigo-100",
      annual: "bg-teal-50 text-teal-700 border-teal-100"
    };
    return (
      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${configs[planType] || configs.monthly}`}>
        {planType}
      </span>
    );
  };

  const isExpiringSoon = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch =
      sub.school_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.subdomain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.billing_email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalActiveRevenue = subscriptions
    .filter(s => s.status === 'active')
    .reduce((sum, s) => sum + parseFloat(s.amount.toString()), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500/20 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 font-medium text-xs tracking-wider">Syncing Revenue Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/20 text-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-full">Revenue Oversight</span>
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Billing & <span className="text-emerald-600 italic font-medium">Subscriptions</span></h1>
            <p className="text-slate-500 mt-1 font-medium">Managing billing cycles for {subscriptions.length} school instances</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-slate-900 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
          >
            <Plus size={18} /> Add Subscription
          </button>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-100 p-4 rounded-2xl text-rose-600 font-bold mb-8 text-sm">
            {error}
          </div>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10 text-sm">
          <StatCard title="Total Subs" value={subscriptions.length} icon={<CreditCard />} color="blue" />
          <StatCard title="Active" value={subscriptions.filter(s => s.status === 'active').length} icon={<Calendar />} color="emerald" />
          <StatCard title="Expiring (30d)" value={subscriptions.filter(s => s.status === 'active' && isExpiringSoon(s.current_period_end)).length} icon={<Calendar />} color="amber" />
          <StatCard title="MRR Estimate" value={`ZMW ${totalActiveRevenue.toLocaleString()}`} icon={<DollarSign />} color="indigo" />
        </div>

        <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/40 border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-50 bg-slate-50/50 flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by school, subdomain, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-500/30 outline-none transition-all shadow-sm"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-6 py-3 bg-white border border-slate-200 rounded-2xl text-xs font-bold uppercase tracking-wider focus:ring-4 focus:ring-emerald-500/5 outline-none appearance-none transition-all cursor-pointer shadow-sm"
              >
                <option value="all">Statuses</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="past_due">Past Due</option>
                <option value="expired">Expired</option>
              </select>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="px-6 py-3 bg-white border border-slate-200 rounded-2xl text-xs font-bold uppercase tracking-wider focus:ring-4 focus:ring-emerald-500/5 outline-none appearance-none transition-all cursor-pointer shadow-sm"
              >
                <option value="all">Plans</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/30 border-b border-slate-50">
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Client Center</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Plan Details</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Revenue</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Lifecycle</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Period</th>
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredSubscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-8 py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center">
                          <CreditCard size={24} className="text-slate-200" />
                        </div>
                        <p className="text-sm font-bold text-slate-300 uppercase tracking-widest">No active billings match selection</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredSubscriptions.map((subscription) => {
                    const expiringSoon = isExpiringSoon(subscription.current_period_end);
                    return (
                      <tr key={subscription.id} className="group hover:bg-slate-50/30 transition-all font-medium">
                        <td className="px-8 py-6">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                              <Building2 size={16} />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-slate-800 tracking-tight">{subscription.school_name}</div>
                              <div className="text-[11px] text-slate-400">{subscription.billing_email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          {getPlanBadge(subscription.plan_type)}
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-sm font-bold text-slate-800">
                            {subscription.currency} {parseFloat(subscription.amount.toString()).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="flex flex-col gap-1.5">
                            {getStatusBadge(subscription.status)}
                            {expiringSoon && subscription.status === 'active' && (
                              <span className="text-[10px] text-amber-500 font-bold uppercase tracking-tighter flex items-center gap-1">
                                <Zap size={10} /> Expiring Soon
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-slate-500">{new Date(subscription.current_period_start).toLocaleDateString()}</span>
                            <ArrowRight size={12} className="text-slate-300" />
                            <span className="text-xs font-bold text-slate-800">{new Date(subscription.current_period_end).toLocaleDateString()}</span>
                          </div>
                        </td>
                        <td className="px-8 py-6 text-right">
                          <div className="flex justify-end gap-1.5 opacity-40 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => navigate(`/superadmin/schools/${subscription.tenant_id}`)} className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-teal-50 hover:text-teal-600 transition-all"><Eye size={16} /></button>
                            <button onClick={() => navigate(`/superadmin/subscriptions/${subscription.id}/edit`)} className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all"><Edit size={16} /></button>
                            <button onClick={() => {/* Delete logic */ }} className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-all"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <AddSubscriptionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          fetchSubscriptions();
        }}
      />
    </div>
  );
};

const StatCard = ({ title, value, icon, color }: any) => {
  const colors: any = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    indigo: "bg-indigo-50 text-indigo-600 border-indigo-100"
  };
  return (
    <div className="bg-white rounded-[2rem] p-7 border border-slate-100 shadow-lg shadow-slate-200/40 group hover:scale-[1.02] transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-2xl border ${colors[color]}`}>
          {React.isValidElement(icon) ? React.cloneElement(icon as React.ReactElement<any>, { size: 20 }) : icon}
        </div>
      </div>
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-1">{title}</p>
        <p className="text-2xl font-bold text-slate-800 tracking-tight">{value}</p>
      </div>
    </div>
  );
};

export default SubscriptionsManagement;
